import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAgreeToProviderTerms, useAuth, useCreateProsProfile,  prosKeys } from '@tark/shared/hooks';
import { agreeToProviderTerms, requestCoachWorkspace, requestOrganizerWorkspace, type OrganizerApplication } from '@tark/shared/lib';
import { Button, Input, Textarea } from '@tark/shared/components';
import type {
  CreateProviderProfilePayload,
  ProviderListingType,
  ProviderProfile,
} from '@tark/shared/types';
import { AlertCircle, ArrowLeft, Building2, Check, CheckCircle2, Image, Trophy, UserRound, Wrench, X } from 'lucide-react';

type ProviderStep = 'basics' | 'details';

const PROVIDER_STEPS: Array<{
  step: ProviderStep;
  label: string;
  title: string;
}> = [
  {
    step: 'basics',
    label: 'Basics',
    title: 'Listing basics',
  },
  {
    step: 'details',
    label: 'Details',
    title: 'Service details',
  },
];

const TYPE_OPTIONS: Array<{
  type: ProviderListingType;
  label: string;
  description: string;
}> = [
  { type: 'ORGANIZER', label: 'Organizer', description: 'Doubles leagues, reviewed by TARK.' },
  {
    type: 'COACH',
    label: 'Coach',
    description: 'Lessons, clinics, and player development.',
  },
  {
    type: 'ACADEMY',
    label: 'Academy',
    description: 'Programs, facilities, and public academy listings.',
  },
  {
    type: 'STRINGER',
    label: 'Stringer',
    description: 'Restringing, setup support, and racquet services.',
  },
];

function toNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getTypeIcon(type: ProviderListingType) {
  switch (type) {
    case 'ORGANIZER':
      return Trophy;
    case 'ACADEMY':
      return Building2;
    case 'COACH':
      return UserRound;
    default:
      return Wrench;
  }
}

function isZipCode(value: string) {
  return /^\d{5}$/.test(value.trim());
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}


function ContactVerificationPill({
  label,
  value,
  verified,
}: {
  label: string;
  value?: string;
  verified: boolean;
}) {
  const ready = Boolean(value && verified);
  return (
    <span
      className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
        ready
          ? 'border-primary/20 bg-primary/10 text-primary'
          : 'border-amber-300 bg-amber-50 text-amber-800'
      }`}
    >
      {ready ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
      <span className="font-semibold">{label}</span>
      <span className="truncate">{value || 'Missing'}</span>
      <span>{ready ? 'Verified' : 'Verify'}</span>
    </span>
  );
}

function StepProgress({
  currentStep,
}: {
  currentStep: ProviderStep;
}) {
  const currentIndex = PROVIDER_STEPS.findIndex((item) => item.step === currentStep);

  return (
    <div className="flex items-center gap-3">
      {PROVIDER_STEPS.map((item, index) => {
        const isActive = item.step === currentStep;
        const isComplete = index < currentIndex;

        return (
          <div key={item.step} className="flex min-w-0 flex-1 items-center gap-3">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${
                isActive || isComplete
                  ? 'border-primary bg-primary [color:var(--color-on-primary)]'
                  : 'border-border bg-surface text-text-subtle'
              }`}
            >
              {isComplete ? <Check className="h-4 w-4" /> : index + 1}
            </div>
            <div className="min-w-0">
              <div className={`text-sm font-semibold ${isActive ? 'text-text' : 'text-text-muted'}`}>
                {item.label}
              </div>
            </div>
            {index < PROVIDER_STEPS.length - 1 ? (
              <div className={`h-px flex-1 ${isComplete ? 'bg-primary/40' : 'bg-border'}`} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function TagInput({
  label,
  values,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  hint?: string;
}) {
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const next = draft.trim().replace(/\s+/g, ' ');
    if (!next) {
      setDraft('');
      return;
    }

    const alreadyExists = values.some((value) => value.toLowerCase() === next.toLowerCase());
    if (!alreadyExists) {
      onChange([...values, next]);
    }
    setDraft('');
  };

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-sm font-medium text-text">{label}</label>
      <div className="rounded-lg border border-border bg-surface p-3 transition-colors hover:border-border-hover focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
        {values.length ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {values.map((value) => (
              <span
                key={value}
                className="inline-flex items-center gap-1 rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-sm text-text"
              >
                {value}
                <button
                  type="button"
                  onClick={() => onChange(values.filter((item) => item !== value))}
                  className="text-text-muted transition-colors hover:text-text"
                  aria-label={`Remove ${value}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={addTag}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
              if (draft.trim()) {
                event.preventDefault();
                addTag();
              }
            }
          }}
          placeholder={placeholder}
          className="w-full border-0 bg-transparent p-0 text-sm text-text placeholder:text-text-subtle focus:outline-none"
        />
      </div>
      {hint ? <p className="mt-1.5 text-xs text-text-muted">{hint}</p> : null}
    </div>
  );
}

function RoleDetailsSection({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-5 border-t border-border pt-6 first:border-t-0 first:pt-0">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2 text-primary">{icon}</div>
        <h3 className="text-sm font-semibold text-text">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function BecomeProviderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [requestId] = useState(() => crypto.randomUUID());
  const created = useRef<ProviderProfile | null>(null);
  const submitting = useRef(false);
  const [finishing, setFinishing] = useState(false);
  const createProfile = useCreateProsProfile(requestId);
  const agreeToTerms = useAgreeToProviderTerms();

  const [currentStep, setCurrentStep] = useState<ProviderStep>('basics');
  const [selectedTypes, setSelectedTypes] = useState<ProviderListingType[]>(['COACH']);
  const [displayName, setDisplayName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [bio, setBio] = useState('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [sports, setSports] = useState<string[]>(['tennis']);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stringerExperienceYears, setStringerExperienceYears] = useState('');
  const [stringerTurnaroundDays, setStringerTurnaroundDays] = useState('');
  const [stringerBasePriceMin, setStringerBasePriceMin] = useState('');
  const [stringerBasePriceMax, setStringerBasePriceMax] = useState('');
  const [stringerBrands, setStringerBrands] = useState<string[]>([]);

  const [coachExperienceYears, setCoachExperienceYears] = useState('');
  const [coachRateMin, setCoachRateMin] = useState('');
  const [coachRateMax, setCoachRateMax] = useState('');
  const [coachLessonTypes, setCoachLessonTypes] = useState<string[]>([]);
  const [coachSpecialties, setCoachSpecialties] = useState<string[]>([]);
  const [coachAvailability, setCoachAvailability] = useState('');

  const [academyName, setAcademyName] = useState('');
  const [academyCourts, setAcademyCourts] = useState('');
  const [academyPrograms, setAcademyPrograms] = useState<string[]>([]);
  const [organizerExperience, setOrganizerExperience] = useState<'NEW' | 'EXPERIENCED'>('NEW');
  const [organizerPlan, setOrganizerPlan] = useState('');
  const [organizerOrgName, setOrganizerOrgName] = useState('');
  const [pastEventName, setPastEventName] = useState('');
  const [pastEventYear, setPastEventYear] = useState('');
  const [pastEventPlayers, setPastEventPlayers] = useState('');
  const [pastEventUrl, setPastEventUrl] = useState('');

  const isSubmitting = finishing || createProfile.isPending || agreeToTerms.isPending;
  const currentStepIndex = PROVIDER_STEPS.findIndex((item) => item.step === currentStep);
  const currentStepMeta = PROVIDER_STEPS[currentStepIndex];
  const accountPhone = user?.phone?.trim() || undefined;
  const accountEmail = user?.email?.trim() || undefined;
  const accountPhoneVerified = Boolean(user?.mobileVerified);
  const accountEmailVerified = Boolean(user?.emailVerified);
  const accountContactReady = Boolean(accountPhone && accountEmail && accountPhoneVerified && accountEmailVerified);
  const accountPhotoUrl = user?.avatarUrl?.trim() || '';
  const accountPreferredContactMethod: CreateProviderProfilePayload['preferredContactMethod'] =
    accountPhone ? 'PHONE' : accountEmail ? 'EMAIL' : undefined;

  const payload = useMemo<CreateProviderProfilePayload>(() => {
    const commonPayload: CreateProviderProfilePayload = {
      displayName: displayName.trim(),
      zipcode: zipcode.trim(),
      providerTypes: selectedTypes,
      bio: bio.trim() || undefined,
      profilePhotoUrl: profilePhotoUrl.trim() || undefined,
      sports: sports.length ? sports : undefined,
      contactPhone: accountPhone,
      contactEmail: accountEmail,
      websiteUrl: websiteUrl.trim() || undefined,
      preferredContactMethod: accountPreferredContactMethod,
    };

    if (selectedTypes.includes('STRINGER')) {
      const turnaroundDays = toNumber(stringerTurnaroundDays);
      commonPayload.stringerDetails = {
        experienceYears: toNumber(stringerExperienceYears),
        turnaroundHours: turnaroundDays !== undefined ? turnaroundDays * 24 : undefined,
        basePriceMin: toNumber(stringerBasePriceMin),
        basePriceMax: toNumber(stringerBasePriceMax),
        stringBrands: stringerBrands.length ? stringerBrands : undefined,
      };
    }

    if (selectedTypes.includes('COACH')) {
      commonPayload.coachDetails = {
        experienceYears: toNumber(coachExperienceYears),
        hourlyRateMin: toNumber(coachRateMin),
        hourlyRateMax: toNumber(coachRateMax),
        lessonTypes: coachLessonTypes.length ? coachLessonTypes : undefined,
        specialties: coachSpecialties.length ? coachSpecialties : undefined,
        availabilityNotes: coachAvailability.trim() || undefined,
      };
    }

    if (selectedTypes.includes('ACADEMY')) {
      commonPayload.academyDetails = {
        academyName: academyName.trim() || undefined,
        numCourts: toNumber(academyCourts),
        programsOffered: academyPrograms.length ? academyPrograms : undefined,
      };
    }

    return commonPayload;
  }, [
    academyCourts,
    academyName,
    academyPrograms,
    bio,
    coachAvailability,
    coachExperienceYears,
    coachLessonTypes,
    coachRateMax,
    coachRateMin,
    coachSpecialties,
    displayName,
    accountEmail,
    accountPhone,
    accountPreferredContactMethod,
    profilePhotoUrl,
    selectedTypes,
    sports,
    stringerBasePriceMax,
    stringerBasePriceMin,
    stringerBrands,
    stringerExperienceYears,
    stringerTurnaroundDays,
    websiteUrl,
    zipcode,
  ]);

  const validateBasicsStep = () => {
    if (!selectedTypes.length) {
      setError('Select at least one role.');
      return false;
    }

    if (!displayName.trim()) {
      setError('Add a display name.');
      return false;
    }

    if (!isZipCode(zipcode)) {
      setError('Enter a valid 5-digit ZIP.');
      return false;
    }

    if (!sports.length) {
      setError('Select at least one sport.');
      return false;
    }

    if (!accountPhone || !accountEmail) {
      setError('Add both phone and email to your TARK account before requesting PRO access.');
      return false;
    }

    if (!accountPhoneVerified || !accountEmailVerified) {
      setError('Verify both phone and email before requesting PRO access.');
      return false;
    }

    return true;
  };

  const validateDetailsStep = () => {
    if (selectedTypes.includes('ORGANIZER')) {
      if (organizerExperience === 'NEW' && (organizerPlan.trim().length < 40 || organizerPlan.trim().length > 500)) {
        setError('Describe your first league or tournament plan in 40–500 characters.');
        return false;
      }
      if (organizerExperience === 'EXPERIENCED' &&
        (!pastEventName.trim() || pastEventName.trim().length > 100 ||
          !Number.isInteger(Number(pastEventYear)) || Number(pastEventYear) < 1950 || Number(pastEventYear) > new Date().getFullYear() ||
          !Number.isInteger(Number(pastEventPlayers)) || Number(pastEventPlayers) < 2 || Number(pastEventPlayers) > 100000)) {
        setError('Add the past event name, year and approximate player count.');
        return false;
      }
      if (pastEventUrl.trim() && (pastEventUrl.trim().length > 300 || !/^https:\/\/[^\s/]+/.test(pastEventUrl.trim()))) {
        setError('Use a secure https:// link for past event evidence.');
        return false;
      }
    }
    if (selectedTypes.includes('STRINGER')) {
      const hasStringerDetails =
        !!stringerExperienceYears.trim() ||
        !!stringerBasePriceMin.trim() ||
        !!stringerBasePriceMax.trim() ||
        !!stringerTurnaroundDays.trim() ||
        stringerBrands.length > 0;
      if (!hasStringerDetails) {
        setError('Add a few stringing details before you submit.');
        return false;
      }
    }

    if (selectedTypes.includes('COACH')) {
      const hasCoachDetails =
        !!coachExperienceYears.trim() ||
        !!coachRateMin.trim() ||
        !!coachRateMax.trim() ||
        coachLessonTypes.length > 0 ||
        coachSpecialties.length > 0;
      if (!hasCoachDetails) {
        setError('Add a few coaching details before you submit.');
        return false;
      }
    }

    if (selectedTypes.includes('ACADEMY')) {
      const hasAcademyDetails =
        !!academyName.trim() ||
        !!academyCourts.trim() ||
        academyPrograms.length > 0;
      if (!hasAcademyDetails) {
        setError('Add a few academy details before you submit.');
        return false;
      }
    }

    if (!acceptTerms) {
      setError('Agree to the marketplace terms.');
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    setError(null);

    if (currentStep === 'basics' && !validateBasicsStep()) {
      return;
    }

    if (currentStep === 'basics') {
      setCurrentStep('details');
    }
  };

  const handleBack = () => {
    if (created.current) {
      navigate(`/pros/manage?providerId=${created.current.id}`);
      return;
    }
    setError(null);

    if (currentStep === 'details') {
      setCurrentStep('basics');
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateDetailsStep()) {
      window.requestAnimationFrame(() => document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      return;
    }

    if (submitting.current) return;
    submitting.current = true; setFinishing(true);
    try {
      created.current ??= await createProfile.mutateAsync(payload);
      await agreeToProviderTerms('1.0', created.current.id);
      if (selectedTypes.includes('ORGANIZER')) {
        const orgName = organizerOrgName.trim() || undefined;
        const organizerApplication: OrganizerApplication = organizerExperience === 'NEW'
          ? { v: 1, experience: 'NEW', plan: organizerPlan.trim(), orgName }
          : { v: 1, experience: 'EXPERIENCED', orgName, pastEvent: {
            name: pastEventName.trim(), year: Number(pastEventYear), players: Number(pastEventPlayers),
            url: pastEventUrl.trim() || undefined,
          } };
        await requestOrganizerWorkspace(organizerApplication, created.current.id);
      } else {
        await requestCoachWorkspace(undefined, created.current.id);
      }
      await queryClient.invalidateQueries({ queryKey: prosKeys.all });
      navigate(`/pros/manage?providerId=${created.current.id}`);
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not create your PRO listing.');
      window.requestAnimationFrame(() => document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    } finally { submitting.current = false; setFinishing(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-24 md:pb-8">
      <div className="pt-2">
        <Link to="/pros">
          <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back to PROs
          </Button>
        </Link>
      </div>

      <section className="rounded-3xl border border-border bg-white p-5 shadow-[var(--shadow-card)] sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary/90">Become a PRO</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-text sm:text-3xl">Set up your listing</h1>
            <p className="mt-2 text-sm leading-6 text-text-muted">We&apos;ll review it before it goes live.</p>
          </div>

          <div className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-text-muted">
            Step {currentStepIndex + 1} of {PROVIDER_STEPS.length}
          </div>
        </div>

        <div className="mt-6">
          <StepProgress currentStep={currentStep} />
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-white p-5 shadow-[var(--shadow-card)] sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold tracking-tight text-text">{currentStepMeta.title}</h2>
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">
            {currentStepMeta.label}
          </span>
        </div>

        {error ? <div className="mt-5"><ErrorBanner message={error} /></div> : null}

        <div className="mt-6 space-y-6">
          {currentStep === 'basics' ? (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                {TYPE_OPTIONS.map((option) => {
                  const Icon = getTypeIcon(option.type);
                  const isSelected = selectedTypes.includes(option.type);
                  return (
                    <button
                      key={option.type}
                      aria-pressed={isSelected}
                      type="button"
                      className={`rounded-2xl border-2 p-4 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/12 shadow-xl shadow-primary/15 ring-2 ring-primary/20'
                          : 'border-border bg-surface hover:border-primary/30 hover:bg-surface-elevated'
                      }`}
                      onClick={() => {
                        setSelectedTypes((current) => (
                          option.type === 'ORGANIZER' ? (isSelected ? [] : ['ORGANIZER']) : current.includes('ORGANIZER') ? [option.type] : isSelected
                            ? current.filter((type) => type !== option.type)
                            : [...current, option.type]
                        ));
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 text-text">
                        <div className={`rounded-xl p-2 ${isSelected ? 'bg-primary [color:var(--color-on-primary)]' : 'bg-primary/10 text-primary'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <span className="font-semibold">{option.label}</span>
                        </div>
                        {isSelected ? (
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                        ) : null}
                      </div>
                      <p className="mt-3 text-sm text-text-muted">{option.description}</p>
                    </button>
                  );
                })}
              </div>

               {selectedTypes.includes('ORGANIZER') && <p className="text-sm text-text-muted">TARK admin approval required. Two free events in total.</p>}
              <div className="grid gap-5 sm:grid-cols-2">
                <Input
                  label="Display name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="North Dallas Tennis Coach"
                  size="lg"
                />
                <Input
                  label="ZIP code"
                  inputMode="numeric"
                  maxLength={5}
                  value={zipcode}
                  onChange={(event) => setZipcode(event.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="75071"
                  size="lg"
                />
              </div>

              <div className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-4 sm:flex-row sm:items-center">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary/10 text-primary">
                  {profilePhotoUrl.trim() ? (
                    <img
                      src={profilePhotoUrl.trim()}
                      alt={displayName || 'Provider photo'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Image className="h-8 w-8" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <Input
                    label="Profile photo URL"
                    value={profilePhotoUrl}
                    onChange={(event) => setProfilePhotoUrl(event.target.value)}
                    placeholder="https://..."
                    size="lg"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {accountPhotoUrl ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProfilePhotoUrl(accountPhotoUrl)}
                      >
                        Use Account Photo
                      </Button>
                    ) : null}
                    {profilePhotoUrl.trim() ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setProfilePhotoUrl('')}
                      >
                        Clear Photo
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <Textarea
                label="Bio (optional)"
                value={bio}
                onChange={(event) => setBio(event.target.value)}
                placeholder="Tell people what you do best and who you help."
              />

              <div>
                <label className="mb-2 block text-sm font-medium text-text">Sports</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 'tennis', label: 'Tennis' },
                    { value: 'pickleball', label: 'Pickleball' },
                  ].map((option) => {
                    const isSelected = sports.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSports((current) => (
                            isSelected
                              ? current.filter((item) => item !== option.value)
                              : [...current, option.value]
                          ));
                        }}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/10 font-semibold text-primary'
                            : 'border-border bg-surface text-text-muted hover:border-primary/25'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-[1fr_0.9fr]">
                <div className="rounded-2xl border border-border bg-surface p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-text">Account contact</p>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        accountContactReady
                          ? 'bg-primary/10 text-primary'
                          : 'bg-amber-50 text-amber-800'
                      }`}
                    >
                      {accountContactReady ? 'Ready for review' : 'Verify first'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-text-muted">
                    Required for review. Player messages stay in TARK.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <ContactVerificationPill label="Phone" value={accountPhone} verified={accountPhoneVerified} />
                    <ContactVerificationPill label="Email" value={accountEmail} verified={accountEmailVerified} />
                  </div>
                  {!accountContactReady ? (
                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <span>Verify phone and email before submitting.</span>
                      <Link to="/profile" className="font-semibold underline underline-offset-2">
                        Open Profile
                      </Link>
                    </div>
                  ) : null}
                </div>
                <Input
                  label="Website (optional)"
                  value={websiteUrl}
                  onChange={(event) => setWebsiteUrl(event.target.value)}
                  placeholder="https://pamotennis.com"
                  size="lg"
                />
              </div>
            </>
          ) : null}

          {currentStep === 'details' ? (
            <>
              {selectedTypes.includes('ORGANIZER') ? (
                <RoleDetailsSection icon={<Trophy className="h-4 w-4" />} title="Organizer background">
                  <p className="text-sm text-text-muted">This background is for TARK review. It won't appear on your public PRO profile. Nothing goes live until TARK approves your application. After approval, create and manage events in Studio on the web. Players join and play in the mobile app.</p>
                  <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Organizer experience">
                    {(['NEW', 'EXPERIENCED'] as const).map((choice) => (
                      <label key={choice} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text">
                        <input type="radio" name="organizer-experience" checked={organizerExperience === choice} onChange={() => setOrganizerExperience(choice)} />
                        {choice === 'NEW' ? 'New organizer' : 'I have run an event'}
                      </label>
                    ))}
                  </div>
                  <Input label="Club or organization (optional)" value={organizerOrgName} onChange={(event) => setOrganizerOrgName(event.target.value)} maxLength={100} size="lg" />
                  {organizerExperience === 'NEW' ? (
                    <div><Textarea label="How will you run your first event?" value={organizerPlan} onChange={(event) => setOrganizerPlan(event.target.value)} maxLength={500} placeholder="Who helps, how courts are confirmed, how results are settled." /><p className="mt-1 text-xs text-text-muted">{organizerPlan.trim().length}/500 characters · minimum 40</p></div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input label="Past event name" value={pastEventName} onChange={(event) => setPastEventName(event.target.value)} size="lg" />
                      <Input label="Year" value={pastEventYear} onChange={(event) => setPastEventYear(event.target.value)} inputMode="numeric" size="lg" />
                      <Input label="Approximate players" value={pastEventPlayers} onChange={(event) => setPastEventPlayers(event.target.value)} inputMode="numeric" size="lg" />
                      <Input label="Event link (optional)" value={pastEventUrl} onChange={(event) => setPastEventUrl(event.target.value)} placeholder="https://" maxLength={300} size="lg" />
                    </div>
                  )}
                  <p className="text-xs text-text-muted">Your answers are visible to you and TARK reviewers only and are removed 90 days after a decision.</p>
                </RoleDetailsSection>
              ) : null}
              {selectedTypes.includes('STRINGER') ? (
                <RoleDetailsSection icon={<Wrench className="h-4 w-4" />} title="Stringer details">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Input
                    label="Experience (years)"
                    value={stringerExperienceYears}
                    onChange={(event) => setStringerExperienceYears(event.target.value)}
                    size="lg"
                  />
                  <Input
                    label="Turnaround (days)"
                    value={stringerTurnaroundDays}
                    onChange={(event) => setStringerTurnaroundDays(event.target.value)}
                    size="lg"
                  />
                  <Input
                    label="Base price min (USD)"
                    value={stringerBasePriceMin}
                    onChange={(event) => setStringerBasePriceMin(event.target.value)}
                    leftIcon={<span className="text-sm font-semibold text-text-muted">$</span>}
                    size="lg"
                  />
                  <Input
                    label="Base price max (USD)"
                    value={stringerBasePriceMax}
                    onChange={(event) => setStringerBasePriceMax(event.target.value)}
                    leftIcon={<span className="text-sm font-semibold text-text-muted">$</span>}
                    size="lg"
                  />
                  <div className="sm:col-span-2">
                    <TagInput
                      label="String brands"
                      values={stringerBrands}
                      onChange={setStringerBrands}
                      placeholder="Type a brand and press Enter"
                    />
                  </div>
                </div>
                </RoleDetailsSection>
              ) : null}

              {selectedTypes.includes('COACH') ? (
                <RoleDetailsSection icon={<UserRound className="h-4 w-4" />} title="Coach details">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Input
                    label="Experience (years)"
                    value={coachExperienceYears}
                    onChange={(event) => setCoachExperienceYears(event.target.value)}
                    size="lg"
                  />
                  <div className="hidden sm:block" />
                  <Input
                    label="Rate min per hour (USD)"
                    value={coachRateMin}
                    onChange={(event) => setCoachRateMin(event.target.value)}
                    leftIcon={<span className="text-sm font-semibold text-text-muted">$</span>}
                    size="lg"
                  />
                  <Input
                    label="Rate max per hour (USD)"
                    value={coachRateMax}
                    onChange={(event) => setCoachRateMax(event.target.value)}
                    leftIcon={<span className="text-sm font-semibold text-text-muted">$</span>}
                    size="lg"
                  />
                  <div className="sm:col-span-2">
                    <TagInput
                      label="Lesson types"
                      values={coachLessonTypes}
                      onChange={setCoachLessonTypes}
                      placeholder="Private, clinic, group lesson"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <TagInput
                      label="Specialties"
                      values={coachSpecialties}
                      onChange={setCoachSpecialties}
                      placeholder="Junior development, match play, technique"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Textarea
                      label="Availability notes"
                      value={coachAvailability}
                      onChange={(event) => setCoachAvailability(event.target.value)}
                      placeholder="Weekday evenings, Saturday mornings, travel lessons available, etc."
                    />
                  </div>
                </div>
                </RoleDetailsSection>
              ) : null}

              {selectedTypes.includes('ACADEMY') ? (
                <RoleDetailsSection icon={<Building2 className="h-4 w-4" />} title="Academy details">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Input
                    label="Academy name"
                    value={academyName}
                    onChange={(event) => setAcademyName(event.target.value)}
                    size="lg"
                  />
                  <Input
                    label="Court count"
                    value={academyCourts}
                    onChange={(event) => setAcademyCourts(event.target.value)}
                    size="lg"
                  />
                  <div className="sm:col-span-2">
                    <TagInput
                      label="Programs offered"
                      values={academyPrograms}
                      onChange={setAcademyPrograms}
                      placeholder="Junior academy, adult clinics, beginner pickleball"
                    />
                  </div>
                </div>
                </RoleDetailsSection>
              ) : null}

              <label className="flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-4 text-sm leading-6 text-text-muted">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(event) => setAcceptTerms(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <span>
                  I agree to the <Link to="/pros-terms" className="font-semibold text-primary">marketplace terms</Link>. My listing will be reviewed before it is published.
                </span>
              </label>

              {!selectedTypes.includes('ORGANIZER') ? <p className="text-sm leading-6 text-text-muted">
                After review, approved listings appear in PROs browse.
              </p> : null}
            </>
          ) : null}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
          {currentStep === 'basics' ? (
            <Link to="/pros">
              <Button variant="outline" size="lg">
                Cancel
              </Button>
            </Link>
          ) : (
            <Button variant="outline" size="lg" onClick={handleBack}>
              Back
            </Button>
          )}

          {currentStep !== 'details' ? (
            <Button size="lg" onClick={handleContinue}>
              Continue
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              fullWidth
              className="sm:w-auto sm:min-w-[15rem]"
            >
              Submit for review
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
